import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  CircularProgress,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  OpenInNew as OpenIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  VerifiedUser as VerifiedUserIcon,
  CurrencyRupee,
  Refresh,
} from '@mui/icons-material';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { RouterLink } from '../../utils/linkNav';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import ExportButton from '../../components/common/ExportButton';

interface Customer {
  id: number;
  customer_code: string;
  company_name: string;
  contact_person: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gstin?: string;
  credit_limit: number;
  is_active: boolean;
  created_at: string;
  gstDetails?: {
    stateCode: string;
    panNumber: string;
    entityType: string;
  };
}

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi'
];

const CustomerPage: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('');

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterState) params.append('state', filterState);

      const data = await api.get(`/sales/customers?${params.toString()}`);
      setCustomers(data.customers || []);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load customers');
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterState]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.customer_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesState = !filterState || customer.state === filterState;
    return matchesSearch && matchesState;
  });

  const hasFilters = !!(searchTerm || filterState);
  const clearFilters = () => {
    setSearchTerm('');
    setFilterState('');
  };

  // Stat cards computed client-side from the loaded list -- null while
  // loading so a slow fetch never briefly reads as "zero customers".
  const totalCount = loading ? null : customers.length;
  const activeCount = loading ? null : customers.filter(c => c.is_active).length;
  const gstRegisteredCount = loading ? null : customers.filter(c => !!c.gstin).length;
  const totalCreditLimit = loading ? null : customers.reduce((sum, c) => sum + Number(c.credit_limit || 0), 0);

  return (
    <Box>
      <PageHeader
        title="Customer Management"
        subtitle="Manage customer database with GSTIN validation and GST compliance"
        actions={
          <>
            <IconButton onClick={fetchCustomers} disabled={loading}>
              <Refresh />
            </IconButton>
            <ExportButton
              rows={customers as any}
              columns={[
                { header: 'Customer Code', key: 'customer_code' }, { header: 'Company Name', key: 'company_name' },
                { header: 'GSTIN', key: 'gstin' }, { header: 'Email', key: 'email' }, { header: 'Phone', key: 'phone' }, { header: 'City', key: 'city' },
              ]}
              filename="customers"
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/sales/customers/new"
            >
              Add Customer
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Customers" value={totalCount} icon={<GroupsIcon />} color="#2563eb" />
        <StatCard title="Active" value={activeCount} icon={<CheckCircleIcon />} color="#059669" />
        <StatCard title="GST Registered" value={gstRegisteredCount} icon={<VerifiedUserIcon />} color="#7c3aed" />
        <StatCard
          title="Total Credit Limit"
          value={totalCreditLimit === null ? null : formatIndianNumber(totalCreditLimit)}
          icon={<CurrencyRupee />}
          color="#d97706"
        />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="State"
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All States</MenuItem>
          {INDIAN_STATES.map(state => (
            <MenuItem key={state} value={state}>{state}</MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
        <Typography variant="body2" color="text.secondary">
          {filteredCustomers.length} of {customers.length} customers
        </Typography>
      </FilterBar>

      {/* Customer Table */}
      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Customer Code', 'Company Name', 'Contact Person', 'Location', 'GSTIN', 'Credit Limit', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchCustomers} />
                  </TableCell>
                </TableRow>
              ) : filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No customers found"
                      hint={searchTerm || filterState ? 'Try adjusting your search criteria' : 'Start by creating your first customer'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/entity/customer/${customer.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={customer.customer_code} tint="#2563eb" icon={<BusinessIcon fontSize="small" />} />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {customer.company_name}
                        </Typography>
                        {customer.email && (
                          <Typography variant="caption" color="text.secondary">
                            {customer.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {customer.contact_person}
                        </Typography>
                        {customer.phone && (
                          <Typography variant="caption" color="text.secondary">
                            {customer.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {customer.city && customer.state ? `${customer.city}, ${customer.state}` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {customer.gstin ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          <Typography variant="body2" fontFamily="monospace">
                            {customer.gstin}
                          </Typography>
                          {customer.gstDetails && (
                            <TypePill label={customer.gstDetails.stateCode} tint="#0284c7" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(customer.credit_limit)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={customer.is_active ? 'Active' : 'Inactive'} tone={customer.is_active ? 'success' : 'neutral'} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          color="primary"
                          component={RouterLink}
                          to={`/sales/customers/${customer.id}/edit`}
                          title="Edit Customer"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          component={RouterLink}
                          to={`/entity/customer/${customer.id}`}
                          title="View 360"
                        >
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default CustomerPage;
